<template>
  <div data-testid="pricing-settings-tab" class="pst-root">
    <v-skeleton-loader v-if="loading" type="article, article" data-testid="pricing-loading"></v-skeleton-loader>
    <template v-else>
      <div v-if="readOnly" class="text-caption warning--text mb-2" data-testid="pricing-readonly-note">
        {{ $t("companies.pricing_read_only") }}
      </div>

      <section v-for="cfg in periodsConfig" :key="cfg.field" class="mb-5">
        <h3 class="mb-1">
          <v-icon small class="mr-1">{{ cfg.icon }}</v-icon>{{ $t(cfg.titleKey) }}
        </h3>
        <div class="text-caption text--secondary mb-2">{{ $t("companies.periods_columns_hint") }}</div>
        <periods-editor :value="local[cfg.field]" :min="cfg.min" :max="cfg.max" :disabled="readOnly"
          :testid-prefix="cfg.testid" @input="onPeriodsInput(cfg.field, $event)"></periods-editor>
      </section>

      <v-divider class="my-4"></v-divider>

      <section class="mb-5">
        <h3 class="mb-1">
          <v-icon small class="mr-1">mdi-white-balance-sunny</v-icon>{{ $t("companies.prices_seasons") }}
        </h3>
        <div class="text-caption text--secondary mb-2">{{ $t("companies.seasons_rows_hint") }}</div>
        <seasons-editor :value="seasons" :disabled="readOnly" :can-delete-saved="canDeleteSeasons"
          @input="onSeasonsInput" @delete="onDeleteSeason"></seasons-editor>
      </section>

      <section class="mb-5">
        <v-select :value="seasonCalcRule" :items="seasonCalcItems" item-text="text" item-value="value"
          :label="$t('companies.season_calc_rule')" dense outlined hide-details="auto" :disabled="readOnly"
          :menu-props="seasonRuleMenuProps"
          class="pst-season-rule" data-testid="pricing-season-calc" @change="onSeasonCalcChange">
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.text }}</v-list-item-title>
              <v-list-item-subtitle class="text-wrap">{{ item.hint }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>
        <div v-if="seasonCalcRuleHint" class="text-caption text--secondary mt-1">{{ seasonCalcRuleHint }}</div>
      </section>

      <!-- FT-217 REQ-09: ценовые группы — после сезонов, вне anyDirty/saveAll:
           пересборка — действие, а не правка формы. Чтение групп — только сотрудникам. -->
      <template v-if="isStaff">
        <v-divider class="my-4"></v-divider>
        <section class="mb-5">
          <price-groups-block></price-groups-block>
        </section>
      </template>

      <!-- FT-216 REQ-01: «Динамическое ценообразование» — свёрнутая панель внизу вкладки, своё сохранение,
           вне anyDirty/saveAll; существующие поля не сдвигаются (панель стоит после них). Видна superadmin/admin. -->
      <dynamic-pricing-panel></dynamic-pricing-panel>

      <v-sheet v-if="anyDirty && !readOnly" class="pst-panel px-4 py-2" elevation="6" rounded
        data-testid="pricing-save-panel">
        <div class="pst-panel__info">
          <div class="text-body-2">
            <v-icon small class="mr-1">mdi-pencil</v-icon>{{ $t("companies.changes_pending") }}: {{ dirtySummary }}
          </div>
          <div v-if="periodsCountChanged" class="text-caption warning--text" data-testid="pricing-count-warning">
            {{ $t("companies.periods_count_warning") }}
          </div>
          <div v-if="seasonsDirty && hasConflicts" class="text-caption error--text" data-testid="pricing-conflict-error">
            {{ $t("errors.seasons_cross") }}
          </div>
        </div>
        <v-btn text class="mr-2" data-testid="pricing-cancel" :disabled="isSaving" @click="cancelAll">
          {{ $t("custom.cancel") }}
        </v-btn>
        <v-btn color="success" :loading="isSaving" data-testid="pricing-save-all" @click="saveAll">
          {{ $t("companies.save_all") }}
        </v-btn>
      </v-sheet>
    </template>
  </div>
</template>

<script>
import PeriodsEditor from "./PeriodsEditor.vue";
import SeasonsEditor from "./SeasonsEditor.vue";
import PriceGroupsBlock from "./PriceGroupsBlock.vue";
import DynamicPricingPanel from "./DynamicPricingPanel.vue";
import {
  seasonsDatesChanged,
  findSeasonConflicts,
} from "../users/seasonHelpers";

const PERIOD_FIELDS = ["periods", "hourly_periods", "weekly_periods", "monthly_periods"];
// Виды цен сезона в порядке ответа deletion_preview (Operations::Fleet::DeleteSeason, CarPrices::KINDS).
const SEASON_PRICE_KINDS = ["prices", "hourly_prices", "weekly_prices", "monthly_prices"];
// Сколько кодов машин показать в предупреждении удаления сезона; остальные — «и ещё N».
const SEASON_DELETE_CODES_SHOWN = 10;

function arraysEqual(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  return left.length === right.length && left.every((v, i) => String(v) === String(right[i]));
}

export default {
  name: "PricingSettingsTab",
  components: { PeriodsEditor, SeasonsEditor, PriceGroupsBlock, DynamicPricingPanel },
  props: {
    company: { type: Object, default: null },
  },
  data() {
    return {
      serverCompany: null,
      local: { periods: null, hourly_periods: null, weekly_periods: null, monthly_periods: null },
      localToggles: { season_start_calc: true, season_end_calc: false },
      seasons: [],
      seasonsLoading: false,
      seasonsRequested: false,
      loadFailed: false,
      isSaving: false,
      periodsConfig: [
        { field: "periods", min: 1, max: 31, icon: "mdi-calendar-today", titleKey: "companies.daily_settings", testid: "daily" },
        { field: "hourly_periods", min: 1, max: 24, icon: "mdi-clock-outline", titleKey: "companies.hourly_settings", testid: "hourly" },
        { field: "weekly_periods", min: 1, max: 52, icon: "mdi-calendar-week", titleKey: "companies.weekly_settings", testid: "weekly" },
        { field: "monthly_periods", min: 1, max: 12, icon: "mdi-calendar-month", titleKey: "companies.monthly_settings", testid: "monthly" },
      ],
    };
  },
  computed: {
    // Периоды и сезоны правят только сотрудники: сервер отказывает гостю, партнёру и агенту (403 staff_only на seasons#create/update).
    readOnly() {
      return !this.isStaff || this.loadFailed;
    },
    // Сохранённый сезон уходит вместе с сезонными ценами всех машин — удаляют только superadmin и admin, как и построчно
    // (CarCard.vue removeSeason*Prices, isAdminPlus). Сервер отказывает остальным 403 admin_plus_required.
    canDeleteSeasons() {
      return !this.readOnly && this.isAdminPlus;
    },
    loading() {
      return !this.serverCompany || this.seasonsLoading;
    },
    dirtyPeriodFields() {
      if (!this.serverCompany) return [];
      return PERIOD_FIELDS.filter((f) => !arraysEqual(this.local[f], this.serverCompany[f]));
    },
    periodsCountChanged() {
      if (!this.serverCompany) return false;
      return this.dirtyPeriodFields.some(
        (f) => (this.local[f] || []).length !== (this.serverCompany[f] || []).length
      );
    },
    seasonRuleMenuProps() {
      // На xs список с подсказками уезжает под нижний системный бар
      // (edge-to-edge webview: innerHeight включает зону под навигацией) — открываем вверх
      return this.$vuetify.breakpoint.xsOnly ? { top: true, offsetY: true } : {};
    },
    seasonCalcItems() {
      return [
        { value: "start", text: this.$t("companies.season_calc_by_start"), hint: this.$t("companies.season_calc_by_start_hint") },
        { value: "end", text: this.$t("companies.season_calc_by_end"), hint: this.$t("companies.season_calc_by_end_hint") },
        { value: "each_day", text: this.$t("companies.season_calc_by_days"), hint: this.$t("companies.season_calc_by_days_hint") },
      ];
    },
    seasonCalcRule() {
      const { season_start_calc: start, season_end_calc: end } = this.localToggles;
      if (start && end) return "each_day";
      if (start) return "start";
      if (end) return "end";
      return null;
    },
    seasonCalcRuleHint() {
      const item = this.seasonCalcItems.find((i) => i.value === this.seasonCalcRule);
      return item ? item.hint : null;
    },
    togglesDirty() {
      if (!this.serverCompany) return false;
      return (
        Boolean(this.serverCompany.season_start_calc) !== this.localToggles.season_start_calc ||
        Boolean(this.serverCompany.season_end_calc) !== this.localToggles.season_end_calc
      );
    },
    seasonsDirty() {
      return this.seasons.some((s) => !s.id) || seasonsDatesChanged(this.seasons);
    },
    hasConflicts() {
      return findSeasonConflicts(this.seasons).length > 0;
    },
    anyDirty() {
      return this.dirtyPeriodFields.length > 0 || this.togglesDirty || this.seasonsDirty;
    },
    dirtySummary() {
      const parts = this.dirtyPeriodFields.map((f) =>
        this.$t(this.periodsConfig.find((c) => c.field === f).titleKey)
      );
      if (this.seasonsDirty) parts.push(this.$t("companies.prices_seasons"));
      if (this.togglesDirty) parts.push(this.$t("companies.season_calc_toggles"));
      return parts.join(" · ");
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(company) {
        if (!company) return;
        this.serverCompany = company;
        this.resetLocalFromCompany();
        if (!this.seasonsRequested) {
          this.seasonsRequested = true;
          this.fetchSeasons();
        }
      },
    },
  },
  methods: {
    resetLocalFromCompany() {
      const src = this.serverCompany;
      const local = {};
      PERIOD_FIELDS.forEach((f) => {
        local[f] = Array.isArray(src[f]) ? src[f].slice() : null;
      });
      this.local = local;
      this.localToggles.season_start_calc = Boolean(src.season_start_calc);
      this.localToggles.season_end_calc = Boolean(src.season_end_calc);
    },
    fetchSeasons() {
      this.seasonsLoading = true;
      return this.axios
        .get(`api/v1/seasons`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        })
        .then((response) => {
          this.seasons = response.data.map((season) => this.rowFromServer(season));
          this.loadFailed = false;
        })
        .catch((error) => {
          this.loadFailed = true;
          this.setError(error, this.$t("errors.seasons_load"));
        })
        .finally(() => {
          this.seasonsLoading = false;
        });
    },
    rowFromServer(season) {
      const [startDay, startMonth] = season.start_date.split(".");
      const [endDay, endMonth] = season.end_date.split(".");
      return {
        id: season.id,
        start_date_day: parseInt(startDay, 10),
        start_date_month: startMonth,
        end_date_day: parseInt(endDay, 10),
        end_date_month: endMonth,
        orig_start_date_day: parseInt(startDay, 10),
        orig_start_date_month: startMonth,
        orig_end_date_day: parseInt(endDay, 10),
        orig_end_date_month: endMonth,
      };
    },
    onPeriodsInput(field, value) {
      this.local = { ...this.local, [field]: value };
    },
    onSeasonsInput(value) {
      this.seasons = value;
    },
    async onDeleteSeason(index) {
      if (this.readOnly) return;
      const season = this.seasons[index];
      if (!season) return;
      if (!season.id) {
        this.seasons = this.seasons.filter((_, i) => i !== index);
        return;
      }
      if (!this.canDeleteSeasons) return;
      // Предупреждение — по факту сервера: сколько машин и каких цен уйдёт вместе с сезоном. Без него не удаляем.
      let preview;
      try {
        const response = await this.axios.get(`/api/v1/seasons/${season.id}/deletion_preview`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        });
        preview = response.data;
      } catch (error) {
        this.setError(error, this.$t("errors.season_delete_preview"));
        return;
      }
      const confirmed = await this.$confirm({
        title: this.$t("custom.you_sure_title"),
        text: `${this.seasonDeleteWarning(preview)} ${this.$t("companies.season_change_warning")}`,
        icon: "mdi-alert-outline",
        iconColor: "warning",
        iconSize: 72,
        iconPosition: "top",
        confirmButtonText: this.$t("custom.you_sure_yes"),
        confirmButtonColor: "primary",
        cancelButtonText: this.$t("custom.you_sure_no"),
        cancelButtonColor: "grey darken-1",
        useFilledButtons: true,
        maxWidth: 450,
      });
      if (!confirmed) return;
      try {
        await this.axios.delete(`/api/v1/seasons/${season.id}`, {
          headers: { Authorization: this.$store.getters.getAuthToken },
        });
        // По id, не по индексу: пока ждали сводку и confirm, строки могли сдвинуться.
        this.seasons = this.seasons.filter((s) => s.id !== season.id);
        this.$notify(this.$t("companies.seasons_delete_success"), "", "success", 3000);
      } catch (error) {
        this.setError(error, this.$t("errors.seasons_delete"));
      }
    },
    seasonDeleteWarning(preview) {
      const { cars = [], prices: counts = {} } = preview;
      // По строкам цен, а не по машинам: строка цены без машины тоже уходит с сезоном.
      const kinds = SEASON_PRICE_KINDS.filter((kind) => counts[kind] > 0);
      if (!kinds.length) return this.$t("companies.season_delete_no_prices");
      const prices = kinds.map((kind) => `${this.$t(`companies.season_price_kinds.${kind}`)} — ${counts[kind]}`).join(", ");
      if (!cars.length) return this.$t("companies.season_delete_affects_no_cars", { prices });
      const shown = cars.slice(0, SEASON_DELETE_CODES_SHOWN).map((car) => car.code || `#${car.id}`);
      const rest = cars.length - shown.length;
      if (rest > 0) shown.push(this.$t("companies.season_delete_more_cars", { count: rest }));
      const carsText = this.$tc("companies.season_delete_cars_plural", cars.length);
      return this.$t("companies.season_delete_affects", { cars: carsText, prices, codes: shown.join(", ") });
    },
    async cancelAll() {
      this.resetLocalFromCompany();
      await this.fetchSeasons();
    },
    async saveAll() {
      if (this.readOnly || this.isSaving) return;
      const seasonsBlocked = this.seasonsDirty && this.hasConflicts;
      const datesChanged = seasonsDatesChanged(this.seasons);

      if (this.seasonsDirty && !seasonsBlocked && datesChanged) {
        const confirmed = await this.$confirm({
          title: this.$t("custom.you_sure_title"),
          text: this.$t("companies.season_change_warning"),
          icon: "mdi-alert-outline",
          iconColor: "warning",
          iconSize: 72,
          iconPosition: "top",
          confirmButtonText: this.$t("custom.confirm"),
          confirmButtonColor: "primary",
          cancelButtonText: this.$t("custom.you_sure_no"),
          cancelButtonColor: "grey darken-1",
          useFilledButtons: true,
          maxWidth: 450,
        });
        if (!confirmed) return;
      }

      this.isSaving = true;
      try {
        await this.saveCompanyFields();
        if (seasonsBlocked) {
          this.$notify(this.$t("errors.seasons_cross"), "", "error", 4000);
        } else if (this.seasonsDirty) {
          await this.saveSeasonRows();
        }
        if (!this.anyDirty) {
          this.$notify(this.$t("companies.periods_saved"), "", "success", 3000);
        }
      } finally {
        this.isSaving = false;
      }
    },
    async saveCompanyFields() {
      const payload = {};
      this.dirtyPeriodFields.forEach((f) => {
        payload[f] = this.local[f];
      });
      if (this.togglesDirty) {
        payload.season_start_calc = this.localToggles.season_start_calc;
        payload.season_end_calc = this.localToggles.season_end_calc;
      }
      if (!Object.keys(payload).length) return;
      try {
        const response = await this.axios.patch(
          `/api/v1/company`,
          { company: payload },
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        );
        this.$store.commit("setCompany", { company: response.data });
        // Baseline — из ответа сервера: prop обновится позже через Vuex.
        this.serverCompany = response.data;
      } catch (error) {
        this.setError(error, this.$t("errors.periods_save"));
      }
    },
    async saveSeasonRows() {
      const failures = [];
      for (let i = 0; i < this.seasons.length; i++) {
        const season = this.seasons[i];
        const body = {
          seasons: {
            start_date: `${season.start_date_day}.${season.start_date_month}`,
            end_date: `${season.end_date_day}.${season.end_date_month}`,
          },
        };
        try {
          if (!season.id) {
            const response = await this.axios.post(`/api/v1/seasons`, body, {
              headers: { Authorization: this.$store.getters.getAuthToken },
            });
            this.$set(this.seasons, i, this.rowFromServer(response.data));
          } else if (this.rowDatesChanged(season)) {
            await this.axios.patch(`/api/v1/seasons/${season.id}`, body, {
              headers: { Authorization: this.$store.getters.getAuthToken },
            });
            this.$set(this.seasons, i, {
              ...season,
              orig_start_date_day: season.start_date_day,
              orig_start_date_month: season.start_date_month,
              orig_end_date_day: season.end_date_day,
              orig_end_date_month: season.end_date_month,
            });
          }
        } catch (error) {
          failures.push(season);
          this.setError(error, this.$t("errors.seasons_update"));
        }
      }
      return failures;
    },
    rowDatesChanged(season) {
      return seasonsDatesChanged([season]);
    },
    onSeasonCalcChange(rule) {
      this.localToggles.season_start_calc = rule === "start" || rule === "each_day";
      this.localToggles.season_end_calc = rule === "end" || rule === "each_day";
    },
  },
};
</script>

<style scoped>
.pst-panel {
  /* fixed, не sticky: overflow-контекст v-tabs-items прячет sticky-элемент */
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 32px);
  max-width: 1100px;
  padding-right: 76px !important; /* не перекрывать чат-бабл */
  display: flex;
  align-items: center;
  z-index: 6;
}
.pst-season-rule {
  max-width: 560px;
}
.pst-panel__info {
  flex: 1;
  min-width: 0;
  text-align: left;
}
@media (max-width: 599px) {
  .pst-panel {
    flex-wrap: wrap;
    padding-right: 72px !important;
    font-size: 12px;
  }
  .pst-panel__info {
    flex-basis: 100%;
    margin-bottom: 4px;
  }
}
</style>
